import React, { useState, useEffect } from 'react';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  deleteDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { getUserOrders, Order } from '../../services/OrderService';
import WishlistSection from './WishlistSection';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [addresses, setAddresses] = useState<any[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [newAddress, setNewAddress] = useState({
    title: '',
    address: '',
    city: '',
    district: '',
    neighborhood: '',
    phone: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Redirect if not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Load user profile and set up real-time listener for addresses and orders
  useEffect(() => {
    if (!currentUser) return;

    let addressesUnsubscribe: () => void;

    const loadProfile = async () => {
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setFirstName(userData.firstName || '');
          setLastName(userData.lastName || '');
        }
      } catch (err) {
        setError('Profil bilgileri yüklenirken hata oluştu.');
      }
    };

    // Set up real-time listener for addresses
    const setupAddressesListener = async () => {
      try {
        const addressesCollectionRef = collection(db, `users/${currentUser.uid}/addresses`);
        addressesUnsubscribe = onSnapshot(addressesCollectionRef, (snapshot) => {
          const addressesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setAddresses(addressesData);
        }, (error) => {
          setError('Adresler yüklenirken hata oluştu.');
        });
      } catch (err) {
        setError('Adresler yüklenirken hata oluştu.');
      }
    };

    // Load orders
    const loadOrders = async () => {
      try {
        const userOrders = await getUserOrders(currentUser.uid);
        setOrders(userOrders);
      } catch (err) {
        setError('Siparişler yüklenirken hata oluştu.');
      }
    };

    // Load all data
    const loadData = async () => {
      await Promise.allSettled([
        loadProfile(),
        loadOrders(),
        setupAddressesListener()
      ]);
      setLoading(false);
    };

    loadData();

    // Cleanup function
    return () => {
      if (addressesUnsubscribe) {
        addressesUnsubscribe();
      }
    };
  }, [currentUser]);

  const handleSaveProfile = async () => {
    if (currentUser) {
      try {
        const userDoc = doc(db, 'users', currentUser.uid);
        await updateDoc(userDoc, {
          firstName,
          lastName
        });
        setMessage('Profil başarıyla güncellendi!');
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setError('Profil güncellenirken hata oluştu.');
      }
    }
  };

  const handleAddAddress = async () => {
    if (currentUser) {
      try {
        const addressesCollection = collection(db, `users/${currentUser.uid}/addresses`);
        await addDoc(addressesCollection, newAddress);

        setMessage('Adres başarıyla eklendi!');
        setNewAddress({
          title: '',
          address: '',
          city: '',
          district: '',
          neighborhood: '',
          phone: ''
        });
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setError('Adres eklenirken hata oluştu.');
      }
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (currentUser && window.confirm('Bu adresi silmek istediğinizden emin misiniz?')) {
      try {
        await deleteDoc(doc(db, `users/${currentUser.uid}/addresses`, addressId));
        setMessage('Adres başarıyla silindi!');
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setError('Adres silinirken hata oluştu.');
      }
    }
  };

  if (!currentUser) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
          <Typography variant="h6">Yükleniyor...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Profil
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Profil Bilgileri" />
            <Tab label="Adresler" />
            <Tab label="Favorilerim" />
            <Tab label="Sipariş Geçmişi" />
          </Tabs>
        </Box>

        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Profile Information Tab */}
        {activeTab === 0 && (
          <Box sx={{ mt: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Kişisel Bilgiler
                </Typography>

                <TextField
                  fullWidth
                  label="Ad"
                  margin="normal"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Soyad"
                  margin="normal"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleSaveProfile}
                >
                  Profili Kaydet
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Addresses Tab */}
        {activeTab === 1 && (
          <Box sx={{ mt: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Adresler
                </Typography>

                <TextField
                  fullWidth
                  label="Adres Başlığı (Örn: Ev, İş)"
                  margin="normal"
                  value={newAddress.title}
                  onChange={(e) => setNewAddress({...newAddress, title: e.target.value})}
                />

                <TextField
                  fullWidth
                  label="Adres"
                  margin="normal"
                  multiline
                  rows={2}
                  value={newAddress.address}
                  onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField
                    fullWidth
                    label="İl"
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    sx={{ flex: '1 1 0' }}
                  />
                  <TextField
                    fullWidth
                    label="İlçe"
                    value={newAddress.district}
                    onChange={(e) => setNewAddress({...newAddress, district: e.target.value})}
                    sx={{ flex: '1 1 0' }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Mahalle"
                  margin="normal"
                  value={newAddress.neighborhood}
                  onChange={(e) => setNewAddress({...newAddress, neighborhood: e.target.value})}
                />

                <TextField
                  fullWidth
                  label="Telefon Numarası"
                  margin="normal"
                  value={newAddress.phone}
                  onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                />

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleAddAddress}
                >
                  Yeni Adres Ekle
                </Button>
              </CardContent>
            </Card>

            {/* Display saved addresses */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Kayıtlı Adresler
              </Typography>

              {addresses.length > 0 ? (
                addresses.map((address) => (
                  <Card key={address.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle1">{address.title}</Typography>
                          <Typography>{address.address}</Typography>
                          <Typography>{address.city}, {address.district}, {address.neighborhood}</Typography>
                          <Typography>Telefon: {address.phone}</Typography>
                        </Box>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteAddress(address.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography>Henüz kayıtlı adresiniz yok.</Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Wishlist Tab */}
        {activeTab === 2 && (
          <Box sx={{ mt: 2 }}>
            <WishlistSection />
          </Box>
        )}

        {/* Order History Tab */}
        {activeTab === 3 && (
          <Box sx={{ mt: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sipariş Geçmişi
                </Typography>

                {orders.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Sipariş ID</TableCell>
                          <TableCell>Tarih</TableCell>
                          <TableCell>Toplam</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell>Ürünler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.id.substring(0, 8)}...</TableCell>
                            <TableCell>{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                            <TableCell>₺{order.total.toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip
                                label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                color={
                                  order.status === 'delivered' ? 'success' :
                                  order.status === 'cancelled' ? 'error' :
                                  order.status === 'pending' ? 'default' :
                                  'primary'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{order.items.length} ürün</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography>Siparişiniz bulunmamaktadır.</Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Profile;